import { atom } from 'jotai';
import { CurrentImageProps, ImageType, VisibilityBoxProps } from '@abyss/3d-viewer';
import * as state from '../atoms';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { getColorBlendingValueMode } from '@/utils/misc';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import { PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import { AllPointOfInterestTemplate } from '@/types';

export const selectedSpherical = atom(
  (get) => get(state.viewerCurrentSpherical),
  (
    get,
    set,
    newValue:
      | CurrentImageProps
      | undefined
      | ((prev: CurrentImageProps | undefined) => CurrentImageProps | undefined)
  ) => {
    if (newValue !== undefined) {
      const locationsMap = get(state.structureLocationsMap);

      const imageLayer = get(state.imageLayer);
      if ('imageType' in newValue && locationsMap && newValue.imageType === ImageType.Spherical) {
        const location = locationsMap.get(newValue.id ?? '');
        if (location && imageLayer) {
          newValue.url = getCloudfrontUrl(
            `${location.baseResourcePath}/${imageLayer.type}/${location.scanKey}.${imageLayer.fileExt}`
          );
        } else {
          newValue.url = getCloudfrontUrl(location?.resourcePath ?? '');
        }
      }
    }

    return set(state.viewerNextCurrentSpherical, newValue);
  }
);

export const blendOpacity = atom(
  (get) => {
    const value = get(state.blendOpacityCombined);

    const mode = getColorBlendingValueMode({
      hasRgb: get(state.hasRgb),
      selectedAssemblyId: !!get(state.selectedAssemblyId),
      selectedSpherical: !!get(selectedSpherical),
    });

    return value[mode];
  },
  (get, set, newValueOrFunc) => {
    const value = get(state.blendOpacityCombined);

    const mode = getColorBlendingValueMode({
      hasRgb: get(state.hasRgb),
      selectedAssemblyId: !!get(state.selectedAssemblyId),
      selectedSpherical: !!get(selectedSpherical),
    });

    const newValue =
      typeof newValueOrFunc === 'number'
        ? newValueOrFunc
        : (newValueOrFunc as (v: number) => number)(value[mode]);
    if (typeof newValue !== 'number') return;

    set(state.blendOpacityCombined, {
      ...value,
      [mode]: newValue,
    });
  }
);

// This atom helps group all the selected paint region ids together.
// These paint region ids come from different sources like a selected custom block
export const allSelectedPaintRegionIds = atom<string[]>((get) => {
  const selectedFilteredPaintRegionIds = get(state.selectedFilteredPaintRegionIds);
  const selectedCustomBlock = get(customBlockState.selectedCustomBlock);

  return selectedCustomBlock?.paintRegionId
    ? [...selectedFilteredPaintRegionIds, selectedCustomBlock.paintRegionId]
    : selectedFilteredPaintRegionIds;
});

export const activeVisibilityBox = atom<VisibilityBoxProps>(
  (get) =>
    get(state.voiVisibilityBox) ??
    get(state.paintRegionVisibilityBox) ??
    get(state.currentVisibilityBox)
);

export const allPaintBlockVoiTemplates = atom<AllPointOfInterestTemplate>((get) => {
  const allPointOfInterestTemplates = get(state.allPointOfInterestTemplates);
  return allPointOfInterestTemplates.filter(
    (template) => template.type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
  );
});
